import {
  ALL_METRICS_FOCUS_OPTIONS,
  ANALYSIS_STRUCTURE_OPTIONS,
  ANALYSIS_TONE_OPTIONS,
  LET_REBOOT_DECIDE_OPTION,
  optionsToLabelMap,
  OUTPUT_FORMAT_OPTIONS,
  RECOMMENDATIONS_OPTIONS,
  TARGET_AUDIENCE_OPTIONS,
  VISUALIZATION_OPTIONS,
} from "./analysisBuilderConstants";

import type { ReportSection } from "./AnalysisSections";
import type { PlayerDataSet } from "./DefinePlayerDataSet";
import type { ReportSettings } from "./GlobalReportSettings";

const ANALYSIS_TONE_LABELS = optionsToLabelMap(ANALYSIS_TONE_OPTIONS);
const TARGET_AUDIENCE_LABELS = optionsToLabelMap(TARGET_AUDIENCE_OPTIONS);
const OUTPUT_FORMAT_LABELS = optionsToLabelMap(OUTPUT_FORMAT_OPTIONS);
const RECOMMENDATIONS_LABELS = optionsToLabelMap(RECOMMENDATIONS_OPTIONS);
const METRICS_FOCUS_LABELS = optionsToLabelMap(ALL_METRICS_FOCUS_OPTIONS);
const ANALYSIS_STRUCTURE_LABELS = optionsToLabelMap(ANALYSIS_STRUCTURE_OPTIONS);
const VISUALIZATION_LABELS = optionsToLabelMap(VISUALIZATION_OPTIONS);

interface GeneratePromptParams {
  playerData: PlayerDataSet;
  settings: ReportSettings;
  sections: ReportSection[];
  playerName: string;
  movementTypeName: string;
}

export function generatePrompt({
  playerData,
  settings,
  sections,
  playerName,
  movementTypeName,
}: GeneratePromptParams): string {
  // Format date selection
  const dateSelection =
    playerData.sessionDates.length > 0
      ? playerData.sessionDates.length === 1
        ? playerData.sessionDates[0].toLocaleDateString()
        : `${playerData.sessionDates.length} sessions selected`
      : "[DATE_SELECTION]";

  // Helper function to convert an unset or "Let Reboot decide" selection
  // into a clearer AI instruction
  const formatForAI = (label: string): string => {
    if (!label || label === "Let Reboot decide") {
      return "Not specified - use your best judgment";
    }
    return label;
  };

  // Get label values and format for AI
  const analysisTone = formatForAI(
    ANALYSIS_TONE_LABELS[settings.analysisTone] || settings.analysisTone,
  );
  const targetAudience = formatForAI(
    TARGET_AUDIENCE_LABELS[settings.targetAudience] || settings.targetAudience,
  );
  // "Let Reboot decide" for output format defaults to HTML Report
  const outputFormat =
    settings.outputFormat === LET_REBOOT_DECIDE_OPTION.value
      ? OUTPUT_FORMAT_LABELS["html-report"]
      : formatForAI(
          OUTPUT_FORMAT_LABELS[settings.outputFormat] || settings.outputFormat,
        );
  const includeRecommendations = formatForAI(
    RECOMMENDATIONS_LABELS[settings.includeRecommendations] ||
      settings.includeRecommendations,
  );

  //let prompt = `You are Reboot Motion's biomechanics analysis assistant, specializing in movement performance analysis. You are being provided with a CSV file containing movement data where each row represents a single movement and columns contain biomechanical metrics and date identifiers.
  let prompt = `
## DATA CONTEXT

**Player:** ${playerName || "[PLAYER_NAME]"}
**Movement Type:** ${movementTypeName || "[MOVEMENT_TYPE]"}
**Date Selection:** ${dateSelection}

Please analyze this data and create a comprehensive report based on the following specifications:

## GLOBAL ANALYSIS PARAMETERS

**Analysis Tone:** ${analysisTone}
**Target Audience:** ${targetAudience}
**Output Format:** ${outputFormat}
**Include Recommendations:** ${includeRecommendations}`;

  if (settings.additionalContext) {
    prompt += `\n**Additional Context:** ${settings.additionalContext}`;
  }

  // A config selection only counts as content if the user picked something
  // specific — an unset value and an explicit "Let Reboot decide" both mean
  // no instruction was given.
  const isMeaningfulSelection = (value: string) =>
    value !== "" && value !== LET_REBOOT_DECIDE_OPTION.value;

  // Add analysis sections if any have content
  const sectionsWithContent = sections.filter(
    (s) =>
      s.title ||
      s.description ||
      isMeaningfulSelection(s.config.metricsFocus) ||
      isMeaningfulSelection(s.config.analysisStructure) ||
      isMeaningfulSelection(s.config.visualization),
  );

  if (sectionsWithContent.length > 0) {
    prompt += `\n\n## ANALYSIS SECTIONS\n\nPlease structure your analysis with the following sections:\n`;

    sectionsWithContent.forEach((section, index) => {
      prompt += `\n### Section ${index + 1}: ${section.title || `Custom Section ${index + 1}`}`;

      const metricsFocusLabel = formatForAI(
        METRICS_FOCUS_LABELS[section.config.metricsFocus] ||
          section.config.metricsFocus,
      );
      prompt += `\n- **Metric Focus:** ${metricsFocusLabel}`;

      const analysisStructureLabel = formatForAI(
        ANALYSIS_STRUCTURE_LABELS[section.config.analysisStructure] ||
          section.config.analysisStructure,
      );
      prompt += `\n- **Analysis Goal:** ${analysisStructureLabel}`;

      const visualizationLabel = formatForAI(
        VISUALIZATION_LABELS[section.config.visualization] ||
          section.config.visualization,
      );
      prompt += `\n- **Visualization:** ${visualizationLabel}`;

      if (section.description) {
        prompt += `\n- **Specific Focus:** ${section.description}`;
      }

      prompt += `\n`;
    });
  }

  prompt += `\n## INSTRUCTIONS

1. Follow the section structure outlined above.
2. For each insight, explain both what the data shows and why it matters for movement performance.
3. Where appropriate, connect findings to actionable next steps.
4. Use clear visualizations to support your analysis.

Please proceed with the analysis.`;

  return prompt;
}
